import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Business as BusinessIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  Star as StarIcon,
  Article as ArticleIcon,
  QuestionAnswer as QAIcon,
  PhotoCamera as PhotoIcon,
  Visibility as VisibilityIcon,
  Compare as CompareIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import api from '../services/api';
import SnapshotSuccessTracker from '../components/SnapshotSuccessTracker';

const Dashboard = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProfiles, setSelectedProfiles] = useState(new Set());
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [expandedAccounts, setExpandedAccounts] = useState(new Set());
  const [selectedAccounts, setSelectedAccounts] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [weeklySnapshotRunning, setWeeklySnapshotRunning] = useState(false);
  const [weeklySnapshotStatus, setWeeklySnapshotStatus] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [capturingSnapshots, setCapturingSnapshots] = useState(false);
  const [exportingAdmins, setExportingAdmins] = useState(false);
  const [exportingLiveAdmins, setExportingLiveAdmins] = useState(false);
  const [liveExportProgress, setLiveExportProgress] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use fast database endpoint by default
      const response = await api.get('/api/profiles/database');
      setProfiles(response.data);
      console.log(`✅ Loaded ${response.data.length} profiles from database`);
      
    } catch (error) {
      console.error('Error fetching profiles from database:', error);
      setError('Failed to load profiles from database');
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshFromAPI = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try the main profiles endpoint for fresh data
      const response = await api.get('/api/profiles');
      setProfiles(response.data);
      console.log(`✅ Refreshed ${response.data.length} profiles from API`);
      setSuccessMessage('Profiles refreshed from API successfully!');
      
    } catch (error) {
      console.error('Error refreshing profiles from API:', error);
      setError('Failed to refresh from API: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleImportProfiles = async () => {
    try {
      setImporting(true);
      setError(null);
      
      const response = await api.post('/api/profiles/import-all');
      
      if (response.data.success) {
        // Refresh profiles after successful import
        await fetchProfiles();
        alert('✅ Profiles imported successfully!');
      } else {
        setError('Import failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error importing profiles:', error);
      setError('Failed to import profiles: ' + (error.response?.data?.error || error.message));
    } finally {
      setImporting(false);
    }
  };

  const handleToggleTracking = async (profileId, currentTracking) => {
    try {
      const response = await api.put(`/api/profiles/${profileId}`, {
        isTracked: !currentTracking
      });
      
      if (response.data.success) {
        // Update the profile in the local state
        setProfiles(prev => prev.map(profile => 
          profile._id === profileId 
            ? { ...profile, isTracked: !currentTracking }
            : profile
        ));
        
        // If we're enabling tracking, automatically capture initial snapshot
        if (!currentTracking) {
          const profile = profiles.find(p => p._id === profileId);
          if (profile) {
            console.log(`📸 Automatically capturing initial snapshot for profile ${profile.name}...`);
            
            try {
              const snapshotResponse = await api.post('/api/snapshots/capture', {
                profileId: profileId
              });
              
              if (snapshotResponse.data.success) {
                console.log(`✅ Initial snapshot captured successfully for "${profile.name}"!`);
              } else {
                console.error('Failed to capture initial snapshot');
              }
            } catch (snapshotError) {
              console.error('Error capturing initial snapshot:', snapshotError);
              console.log('⚠️ Profile is now tracked, but initial snapshot capture failed. You can trigger snapshots manually later.');
            }
          }
        }
      }
    } catch (error) {
      console.error('Error toggling tracking:', error);
      setError('Failed to update tracking status');
    }
  };

  const handleToggleAccountTracking = async (accountId, currentTracking) => {
    try {
      // Get all profiles for this account
      const accountProfiles = profiles.filter(profile => profile.accountId === accountId);
      
      // Update all profiles in this account
      const updatePromises = accountProfiles.map(profile => 
        api.put(`/api/profiles/${profile._id}`, {
          isTracked: !currentTracking
        })
      );
      
      const results = await Promise.allSettled(updatePromises);
      
      // Check if all updates were successful
      const allSuccessful = results.every(result => 
        result.status === 'fulfilled' && result.value.data
      );
      
      if (allSuccessful) {
        // Update all profiles in state
        setProfiles(prev => prev.map(profile => 
          profile.accountId === accountId 
            ? { ...profile, isTracked: !currentTracking }
            : profile
        ));
        
        // Update selected accounts state
        setSelectedAccounts(prev => {
          const newSet = new Set(prev);
          if (!currentTracking) {
            newSet.add(accountId);
          } else {
            newSet.delete(accountId);
          }
          return newSet;
        });
        
        console.log(`✅ ${accountProfiles.length} profiles in account ${accountId} ${!currentTracking ? 'tracked' : 'untracked'}`);
        
        // Show success message
        if (!currentTracking) {
          setError(null); // Clear any previous errors
          setSuccessMessage(`${accountProfiles.length} profiles in account ${accountId} are now being tracked!`);
          setTimeout(() => setSuccessMessage(null), 5000); // Clear after 5 seconds
        }
        
        // If we're enabling tracking, automatically capture initial snapshots
        if (!currentTracking && accountProfiles.length > 0) {
          console.log(`📸 Automatically capturing initial snapshots for ${accountProfiles.length} profiles in account ${accountId}...`);
          setCapturingSnapshots(true);
          
          try {
            const profileIds = accountProfiles.map(profile => profile._id);
            const snapshotResponse = await api.post('/api/snapshots/capture', {
              profileIds: profileIds
            });
            
            if (snapshotResponse.data.success) {
              console.log(`✅ Initial snapshots captured successfully for ${accountProfiles.length} profiles in account ${accountId}!`);
              setSuccessMessage(`✅ ${accountProfiles.length} profiles tracked and initial snapshots captured!`);
            } else {
              console.error('Failed to capture initial snapshots');
              setError('Profiles tracked but initial snapshots failed. You can trigger snapshots manually later.');
            }
          } catch (snapshotError) {
            console.error('Error capturing initial snapshots:', snapshotError);
            setError('Profiles tracked but initial snapshot capture failed. You can trigger snapshots manually later.');
          } finally {
            setCapturingSnapshots(false);
          }
        }
      } else {
        setError(`Failed to update some profiles in account ${accountId}`);
      }
    } catch (error) {
      console.error('Error toggling account tracking:', error);
      setError('Failed to update account tracking status');
    }
  };

  const getAccountTrackingStatus = useCallback((accountId) => {
    const accountProfiles = profiles.filter(profile => profile.accountId === accountId);
    const trackedCount = accountProfiles.filter(profile => profile.isTracked).length;
    const totalCount = accountProfiles.length;
    
    if (trackedCount === 0) return 'none';
    if (trackedCount === totalCount) return 'all';
    return 'partial';
  }, [profiles]);

  const getAccountTrackingText = useCallback((accountId) => {
    const status = getAccountTrackingStatus(accountId);
    const accountProfiles = profiles.filter(profile => profile.accountId === accountId);
    const trackedCount = accountProfiles.filter(profile => profile.isTracked).length;
    const totalCount = accountProfiles.length;
    
    switch (status) {
      case 'none': return `Track (${totalCount})`;
      case 'all': return `Untrack (${totalCount})`;
      case 'partial': return `Track All (${trackedCount}/${totalCount})`;
      default: return 'Track';
    }
  }, [profiles, getAccountTrackingStatus]);

  const getSelectedAccountsProfileCount = useCallback(() => {
    return profiles.filter(profile => selectedAccounts.has(profile.accountId)).length;
  }, [profiles, selectedAccounts]);

  // Helper to check if a profile has snapshots (for visual indicators)
  const hasSnapshots = useCallback((profileId) => {
    // This would need to be implemented with actual snapshot data
    // For now, we'll assume profiles might have snapshots
    return true; // Placeholder - could be enhanced with actual snapshot count
  }, []);

  const handleTriggerWeeklySnapshot = async () => {
    try {
      setWeeklySnapshotRunning(true);
      setError(null);
      
      // Get profiles from selected accounts only
      const selectedAccountProfiles = profiles.filter(profile => 
        selectedAccounts.has(profile.accountId)
      );
      
      if (selectedAccounts.size === 0) {
        setError('Please select at least one account to track');
        return;
      }
      
      console.log(`📸 Triggering weekly snapshots for ${selectedAccountProfiles.length} profiles from ${selectedAccounts.size} selected accounts`);
      
      // Get profile IDs from selected accounts
      const profileIds = selectedAccountProfiles.map(profile => profile._id);
      
      const response = await api.post('/api/snapshots/capture', {
        profileIds: profileIds
      });
      
      if (response.data.success) {
        alert(`✅ Weekly snapshot triggered successfully for ${selectedAccountProfiles.length} profiles from ${selectedAccounts.size} accounts!`);
      }
    } catch (error) {
      console.error('Error triggering weekly snapshot:', error);
      setError('Failed to trigger weekly snapshot: ' + (error.response?.data?.error || error.message));
    } finally {
      setWeeklySnapshotRunning(false);
    }
  };

  const fetchWeeklySnapshotStatus = async () => {
    try {
      const response = await api.get('/api/snapshots/weekly/status');
      setWeeklySnapshotStatus(response.data);
    } catch (error) {
      console.error('Error fetching weekly snapshot status:', error);
    }
  };

  const handleExportAdmins = async () => {
    try {
      setExportingAdmins(true);
      setError(null);
      
      console.log('📊 Exporting admin data as CSV...');
      
      // Call the export endpoint
      const response = await api.get('/api/profiles/admins/export', {
        responseType: 'blob' // Important for file downloads
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'gbp-admin-data.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Admin data exported successfully');
      setSuccessMessage('Admin data exported successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
      
    } catch (error) {
      console.error('❌ Error exporting admin data:', error);
      setError('Failed to export admin data: ' + (error.response?.data?.error || error.message));
    } finally {
      setExportingAdmins(false);
    }
  };

  const handleExportLiveAdmins = async () => {
    try {
      setExportingLiveAdmins(true);
      setError(null);
      setLiveExportProgress({ current: 0, total: 0 });

      // Chunked export — DigitalOcean App Platform kills HTTP requests around ~100s,
      // so one giant live export cannot finish for 1000+ locations.
      const chunkSize = 20;
      let offset = 0;
      let total = null;
      let header = null;
      const csvParts = [];
      let totalRows = 0;
      let totalErrors = 0;

      console.log('📡 Starting chunked live admins export...');
      setSuccessMessage('Fetching live admins in chunks (pauses other dashboard requests)...');

      while (true) {
        console.log(`📡 Requesting chunk offset=${offset}, limit=${chunkSize}`);
        const response = await api.get('/api/profiles/admins/export/live/chunk', {
          params: { offset, limit: chunkSize },
          timeout: 90000
        });

        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Chunk export failed');
        }

        const {
          header: chunkHeader,
          csvRows,
          rowCount,
          errorCount,
          nextOffset,
          total: chunkTotal,
          done,
          locationsProcessed
        } = response.data;

        if (!header && chunkHeader) header = chunkHeader;
        if (csvRows) csvParts.push(csvRows);
        totalRows += rowCount || 0;
        totalErrors += errorCount || 0;
        total = chunkTotal;
        offset = nextOffset;

        const current = Math.min(offset, total || offset);
        setLiveExportProgress({ current, total: total || current });
        setSuccessMessage(
          `Live admins export: ${current} / ${total || '?'} locations (${totalRows} CSV rows so far)...`
        );
        console.log(
          `✅ Chunk complete — locations +${locationsProcessed}, rows ${totalRows}, nextOffset ${nextOffset}, done ${done}`
        );

        if (done) break;
      }

      const csvContent = [header, ...csvParts.filter(Boolean)].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `gbp-live-admins-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`✅ Live admin export finished — ${totalRows} rows, ${totalErrors} location errors`);
      setSuccessMessage(
        `Live admin export complete! ${totalRows} rows across ${total} locations` +
          (totalErrors ? ` (${totalErrors} location errors recorded in CSV).` : '.')
      );
      setTimeout(() => setSuccessMessage(null), 10000);
    } catch (error) {
      console.error('❌ Error exporting live admin data:', error);
      const message = error.response?.data?.error || error.response?.data?.details || error.message;
      setError('Failed to export live admin data: ' + message);
      setSuccessMessage(null);
    } finally {
      setExportingLiveAdmins(false);
      setLiveExportProgress(null);
    }
  };

  const handleAutoExportAdmins = async () => {
    try {
      setExportingAdmins(true);
      setError(null);
      
      console.log('📊 Starting auto-paginated CSV export...');
      
      const response = await api.get('/api/profiles/admins/export/auto?pageSize=50&maxFiles=20');
      
      if (response.data.success) {
        const { files, summary } = response.data;
        
        console.log(`✅ Auto-export completed: ${summary.totalProcessed} records across ${summary.filesCreated} files`);
        
        // Download each CSV file
        files.forEach((file, index) => {
          if (file.status === 'completed' && file.csvContent) {
            setTimeout(() => {
              const blob = new Blob([file.csvContent], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = file.fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            }, index * 1000); // 1 second delay between downloads
          }
        });
        
        setSuccessMessage(`Auto-export completed! Downloaded ${summary.filesCreated} CSV files with ${summary.totalProcessed} total records.`);
        setTimeout(() => setSuccessMessage(null), 10000);
        
      } else {
        throw new Error(response.data.error || 'Auto-export failed');
      }
      
    } catch (error) {
      console.error('❌ Error in auto-paginated export:', error);
      setError('Failed to complete auto-paginated export: ' + (error.response?.data?.error || error.message));
    } finally {
      setExportingAdmins(false);
    }
  };

  useEffect(() => {
    fetchWeeklySnapshotStatus();
  }, []);

  const handleProfileClick = useCallback((profileId) => {
    navigate(`/profile/${profileId}`);
  }, [navigate]);

  const handleProfileSelect = useCallback((profileId) => {
    setSelectedProfiles(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(profileId)) {
        newSelected.delete(profileId);
      } else {
        newSelected.add(profileId);
      }
      return newSelected;
    });
  }, []);

  const handleCompareSelected = useCallback(() => {
    if (selectedProfiles.size === 2) {
      const profileIds = Array.from(selectedProfiles);
      navigate(`/compare?profile1=${profileIds[0]}&profile2=${profileIds[1]}`);
    }
  }, [selectedProfiles, navigate]);

  const filteredProfiles = useMemo(() => {
    return profiles.filter(profile => {
      const matchesSearch = profile.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           profile.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           profile.address?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [profiles, searchTerm]);

  // Group profiles by accountId
  const groupedProfiles = useMemo(() => {
    const groups = {};
    filteredProfiles.forEach(profile => {
      const groupId = profile.accountId || 'Unknown Account';
      if (!groups[groupId]) {
        groups[groupId] = [];
      }
      groups[groupId].push(profile);
    });
    return groups;
  }, [filteredProfiles]);

  const getStatusColor = useCallback((isActive) => isActive ? 'success' : 'error', []);
  const getStatusText = useCallback((isActive) => isActive ? 'Active' : 'Inactive', []);

  const getDataIcon = useCallback((dataType) => {
    switch (dataType) {
      case 'business': return <BusinessIcon fontSize="small" />;
      case 'reviews': return <StarIcon fontSize="small" />;
      case 'posts': return <ArticleIcon fontSize="small" />;
      case 'qa': return <QAIcon fontSize="small" />;
      case 'photos': return <PhotoIcon fontSize="small" />;
      default: return <AssessmentIcon fontSize="small" />;
    }
  }, []);

  const getCategoryDisplay = useCallback((profile) => {
    // Prioritize the primary category field
    if (profile.category) {
      return profile.category;
    }
    // Fallback to the first category in the categories array
    if (profile.categories && profile.categories.length > 0) {
      return profile.categories[0];
    }
    return 'Uncategorized';
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedProfiles(prev => {
      if (prev.size === filteredProfiles.length) {
        return new Set();
      } else {
        return new Set(filteredProfiles.map(p => p._id));
      }
    });
  }, [filteredProfiles]);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 'bold',
              color: '#ffffff',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              mb: 2
            }}
            gutterBottom
          >
            GBP Database Dashboard
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
              mb: 4
            }}
          >
            Loading your business profiles...
          </Typography>
        </Box>

        {/* Loading Animation */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '400px',
          gap: 3
        }}>
          <Box sx={{ 
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <CircularProgress 
              size={80} 
              thickness={4}
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }}
            />
            <Box sx={{
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <BusinessIcon sx={{ 
                fontSize: 28, 
                color: 'rgba(255, 255, 255, 0.9)',
                animation: 'pulse 2s ease-in-out infinite'
              }} />
            </Box>
          </Box>
          
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              textAlign: 'center',
              fontWeight: 300
            }}
          >
            Loading profiles from database...
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.6)',
              textAlign: 'center',
              maxWidth: 400
            }}
          >
            This should only take a moment. If it's taking longer than usual, try refreshing the page.
          </Typography>
        </Box>

        {/* Loading Stats Placeholder */}
        <Box sx={{ mt: 6 }}>
          <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
            {[...Array(4)].map((_, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card className="modern-card" sx={{ 
                  textAlign: 'center', 
                  p: 3, 
                  height: '100%',
                  opacity: 0.7,
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <Skeleton 
                    variant="circular" 
                    width={56} 
                    height={56} 
                    sx={{ mx: 'auto', mb: 2, bgcolor: 'rgba(255, 255, 255, 0.2)' }}
                  />
                  <Skeleton 
                    variant="text" 
                    width="60%" 
                    height={32} 
                    sx={{ mx: 'auto', mb: 1, bgcolor: 'rgba(255, 255, 255, 0.2)' }}
                  />
                  <Skeleton 
                    variant="text" 
                    width="40%" 
                    height={24} 
                    sx={{ mx: 'auto', bgcolor: 'rgba(255, 255, 255, 0.1)' }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            fontWeight: 'bold',
            color: '#ffffff',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            mb: 2
          }}
          gutterBottom
        >
          GBP Database Dashboard
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.9)',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
            mb: 4
          }}
        >
          Manage and analyze your Google Business Profile data with powerful insights
        </Typography>
      </Box>

      {/* Import Profiles Button */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Button
          variant="contained"
          onClick={handleImportProfiles}
          disabled={importing}
          startIcon={importing ? <Skeleton variant="circular" width={20} height={20} /> : <BusinessIcon />}
          className="modern-button"
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            px: 4,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 'bold',
            mr: 2,
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
            },
            '&:disabled': {
              background: 'rgba(102, 126, 234, 0.5)',
              transform: 'none'
            }
          }}
        >
          {importing ? 'Importing Profiles...' : 'Import All Profiles'}
        </Button>
        
        <Button
          variant="outlined"
          onClick={refreshFromAPI}
          disabled={loading}
          startIcon={loading ? <Skeleton variant="circular" width={20} height={20} /> : <RefreshIcon />}
          className="modern-button"
          sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            px: 4,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 'bold',
            mr: 2,
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(255, 255, 255, 0.2)'
            },
            '&:disabled': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)',
              transform: 'none'
            }
          }}
        >
          {loading ? 'Refreshing...' : 'Refresh from API'}
        </Button>
        
        <Tooltip title="Download CSV with admin details for each business location">
          <Button
            variant="contained"
            onClick={handleExportAdmins}
            disabled={exportingAdmins || exportingLiveAdmins}
            startIcon={exportingAdmins ? <Skeleton variant="circular" width={20} height={20} /> : <DownloadIcon />}
            className="modern-button"
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #e085f0 0%, #e54b5b 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(240, 147, 251, 0.3)'
              },
              '&:disabled': {
                background: 'rgba(240, 147, 251, 0.5)',
                transform: 'none'
              }
            }}
          >
            {exportingAdmins ? 'Exporting Admins...' : 'Export Admin Data (100 records)'}
          </Button>
        </Tooltip>
        
        <Tooltip title="Download ALL admin data in multiple CSV files automatically">
          <Button
            variant="contained"
            color="secondary"
            onClick={handleAutoExportAdmins}
            disabled={exportingAdmins || exportingLiveAdmins}
            startIcon={exportingAdmins ? <Skeleton variant="circular" width={20} height={20} /> : <DownloadIcon />}
            className="modern-button"
            sx={{
              ml: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              px: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                transform: 'translateY(-2px)'
              },
              '&:disabled': {
                background: 'rgba(102, 126, 234, 0.5)',
                transform: 'none'
              }
            }}
          >
            {exportingAdmins ? 'Auto-Exporting...' : 'Auto-Export All Data (Multiple Files)'}
          </Button>
        </Tooltip>

        <Tooltip title="Call the live GBP admins API for every location and download one CSV">
          <Button
            variant="contained"
            onClick={handleExportLiveAdmins}
            disabled={exportingLiveAdmins || exportingAdmins}
            startIcon={exportingLiveAdmins ? <Skeleton variant="circular" width={20} height={20} /> : <DownloadIcon />}
            className="modern-button"
            sx={{
              ml: 2,
              mt: { xs: 2, md: 0 },
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              color: 'white',
              px: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0e8a7f 0%, #2fd66e 100%)',
                boxShadow: '0 6px 20px rgba(17, 153, 142, 0.35)',
                transform: 'translateY(-2px)'
              },
              '&:disabled': {
                background: 'rgba(17, 153, 142, 0.5)',
                transform: 'none'
              }
            }}
          >
            {exportingLiveAdmins
              ? (liveExportProgress?.total
                  ? `Exporting ${liveExportProgress.current}/${liveExportProgress.total}...`
                  : 'Fetching Live Admins...')
              : 'Export Live Admins CSV'}
          </Button>
        </Tooltip>
        
        {(importing || exportingLiveAdmins) && (
          <Typography variant="body2" color="rgba(255, 255, 255, 0.8)" sx={{ mt: 1 }}>
            {exportingLiveAdmins
              ? 'Chunked live export in progress — keep this tab open. Snapshot metrics are paused to avoid conflicts.'
              : 'This may take a few minutes...'}
          </Typography>
        )}
      </Box>

      {/* Weekly Snapshot Management */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Button
          variant="contained"
          onClick={handleTriggerWeeklySnapshot}
          disabled={weeklySnapshotRunning}
          startIcon={weeklySnapshotRunning ? <Skeleton variant="circular" width={20} height={20} /> : <TimelineIcon />}
          className="modern-button"
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#ffffff',
            px: 4,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 'bold',
            mr: 2,
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
            },
            '&:disabled': {
              background: 'rgba(102, 126, 234, 0.5)',
              transform: 'none'
            }
          }}
        >
          {weeklySnapshotRunning 
            ? 'Running Weekly Snapshots...' 
            : selectedAccounts.size > 0 
              ? `Trigger Weekly Snapshots (${selectedAccounts.size} accounts, ${getSelectedAccountsProfileCount()} profiles)`
              : 'Trigger Weekly Snapshots (Select Accounts)'
          }
        </Button>
        
        {weeklySnapshotStatus && (
          <Typography variant="body2" color="rgba(255, 255, 255, 0.8)" sx={{ mt: 1 }}>
            Next scheduled run: {weeklySnapshotStatus.nextRun} ({weeklySnapshotStatus.timezone})
          </Typography>
        )}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4, justifyContent: 'center' }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="modern-card" sx={{ textAlign: 'center', p: 3, height: '100%' }}>
            <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <BusinessIcon fontSize="large" />
            </Avatar>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1, color: '#1a202c' }}>
              {profiles.length}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Profiles
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className="modern-card" sx={{ textAlign: 'center', p: 3, height: '100%' }}>
            <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <TrendingUpIcon fontSize="large" />
            </Avatar>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1, color: '#1a202c' }}>
              {profiles.filter(p => p.isActive).length}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Active Profiles
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className="modern-card" sx={{ textAlign: 'center', p: 3, height: '100%' }}>
            <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <TimelineIcon fontSize="large" />
            </Avatar>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1, color: '#1a202c' }}>
              {profiles.filter(p => !p.isActive).length}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Inactive Profiles
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className="modern-card" sx={{ textAlign: 'center', p: 3, height: '100%' }}>
            <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <AssessmentIcon fontSize="large" />
            </Avatar>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1, color: '#1a202c' }}>
              {profiles.length > 0 ? Math.round(profiles.filter(p => p.isActive).length / profiles.length * 100) : 0}%
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Active Rate
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className="modern-card" sx={{ textAlign: 'center', p: 3, height: '100%' }}>
            <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <TimelineIcon fontSize="large" />
            </Avatar>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1, color: '#1a202c' }}>
              {profiles.filter(p => p.isTracked).length}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Tracked Profiles
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className="modern-card" sx={{ textAlign: 'center', p: 3, height: '100%' }}>
            <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <BusinessIcon fontSize="large" />
            </Avatar>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1, color: '#1a202c' }}>
              {selectedAccounts.size}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Selected Accounts
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className="modern-card" sx={{ textAlign: 'center', p: 3, height: '100%' }}>
            <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <TimelineIcon fontSize="large" />
            </Avatar>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1, color: '#1a202c' }}>
              {profiles.filter(p => p.isTracked && hasSnapshots(p._id)).length}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Tracked with Snapshots
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Snapshot Success Tracker */}
      <Box sx={{ mb: 4 }}>
        <SnapshotSuccessTracker profiles={profiles} paused={exportingLiveAdmins} />
      </Box>

      {/* Search Icon */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        {!searchExpanded ? (
          <IconButton
            onClick={() => setSearchExpanded(true)}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              color: '#1a202c',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            <SearchIcon />
          </IconButton>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: 2,
            px: 2,
            py: 1,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            animation: 'slideIn 0.3s ease'
          }}>
            <SearchIcon sx={{ color: '#1a202c' }} />
            <TextField
              placeholder="Search profiles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onBlur={() => {
                if (!searchTerm) {
                  setSearchExpanded(false);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSearchTerm('');
                  setSearchExpanded(false);
                }
              }}
              autoFocus
              variant="standard"
              sx={{
                '& .MuiInput-root': {
                  color: '#1a202c',
                  fontSize: '16px',
                  '&:before': { borderBottom: 'none' },
                  '&:after': { borderBottom: 'none' },
                  '&:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                },
                '& .MuiInput-input': {
                  padding: '8px 0',
                  '&::placeholder': {
                    color: '#4a5568',
                    opacity: 1
                  }
                }
              }}
            />
            <IconButton
              size="small"
              onClick={() => {
                setSearchTerm('');
                setSearchExpanded(false);
              }}
              sx={{ color: '#1a202c' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Action Buttons */}
      {selectedProfiles.size > 0 && (
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<CompareIcon />}
            onClick={handleCompareSelected}
            disabled={selectedProfiles.size !== 2}
            className="modern-button"
          >
            Compare Selected ({selectedProfiles.size})
          </Button>
          <Typography variant="body2" color="textSecondary" sx={{ alignSelf: 'center' }}>
            {selectedProfiles.size === 2 ? 'Ready to compare' : 'Select exactly 2 profiles to compare'}
          </Typography>
        </Box>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Success Display */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 4 }}>
          {successMessage}
        </Alert>
      )}

      {/* Profiles Table */}
      <Card className="modern-card">
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <Typography variant="h6" sx={{ color: '#1a202c', fontWeight: 'bold' }}>
              Business Profiles ({filteredProfiles.length})
            </Typography>
          </Box>
          
          {filteredProfiles.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No profiles found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {searchTerm 
                  ? 'Try adjusting your search criteria'
                  : 'No profiles have been added yet'
                }
              </Typography>
            </Box>
          ) : (
            <TableContainer className="modern-table-container" sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedProfiles.size > 0 && selectedProfiles.size < filteredProfiles.length}
                        checked={selectedProfiles.size === filteredProfiles.length && filteredProfiles.length > 0}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Business Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Tracking</TableCell>
                    <TableCell>Data Types</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(groupedProfiles).map(([accountId, accountProfiles]) => (
                    <React.Fragment key={accountId}>
                      {/* Account Header Row */}
                      <TableRow 
                        sx={{ 
                          backgroundColor: 'rgba(102, 126, 234, 0.05)',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(102, 126, 234, 0.1)'
                          }
                        }}
                        onClick={() => {
                          setExpandedAccounts(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(accountId)) {
                              newSet.delete(accountId);
                            } else {
                              newSet.add(accountId);
                            }
                            return newSet;
                          });
                        }}
                      >
                        <TableCell colSpan={8}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <BusinessIcon color="primary" />
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a202c' }}>
                                {accountId} ({accountProfiles.length} profiles)
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={capturingSnapshots}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const status = getAccountTrackingStatus(accountId);
                                  const isCurrentlyTracked = status === 'all';
                                  handleToggleAccountTracking(accountId, isCurrentlyTracked);
                                }}
                                sx={{
                                  fontSize: '0.75rem',
                                  px: 1.5,
                                  py: 0.5,
                                  minWidth: 'auto',
                                  height: 28,
                                  borderColor: getAccountTrackingStatus(accountId) === 'all' ? 'success.main' : 'grey.400',
                                  color: getAccountTrackingStatus(accountId) === 'all' ? 'success.main' : 'grey.600',
                                  '&:hover': {
                                    borderColor: getAccountTrackingStatus(accountId) === 'all' ? 'success.dark' : 'primary.main',
                                    color: getAccountTrackingStatus(accountId) === 'all' ? 'success.dark' : 'primary.main',
                                  }
                                }}
                                                              >
                                  {capturingSnapshots ? 'Capturing...' : getAccountTrackingText(accountId)}
                                </Button>
                              <IconButton size="small">
                                {expandedAccounts.has(accountId) ? 
                                  <ChevronLeftIcon /> : 
                                  <ChevronRightIcon />
                                }
                              </IconButton>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                      
                      {/* Profile Rows for this Account */}
                      {expandedAccounts.has(accountId) && accountProfiles.map((profile) => (
                        <TableRow key={profile._id} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedProfiles.has(profile._id)}
                              onChange={() => handleProfileSelect(profile._id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                                <BusinessIcon fontSize="small" />
                              </Avatar>
                              <Box>
                                <Typography 
                                  variant="body1" 
                                  sx={{ 
                                    fontWeight: 'bold', 
                                    color: '#1a202c',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      color: 'primary.main',
                                      textDecoration: 'underline'
                                    }
                                  }}
                                  onClick={() => handleProfileClick(profile._id)}
                                >
                                  {profile.name}
                                </Typography>
                                {profile.storeCode && (
                                  <Typography variant="caption" color="textSecondary">
                                    {profile.storeCode}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getCategoryDisplay(profile)}
                              size="small"
                              variant="outlined"
                              className="modern-chip"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 200 }}>
                              {profile.address || 'No address'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusText(profile.isActive)}
                              color={getStatusColor(profile.isActive)}
                              size="small"
                              className="modern-chip"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={profile.isTracked ? 'Tracked' : 'Not Tracked'}
                              color={profile.isTracked ? 'success' : 'default'}
                              size="small"
                              className="modern-chip"
                              onClick={() => handleToggleTracking(profile._id, profile.isTracked)}
                              sx={{ cursor: 'pointer' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {['business', 'reviews', 'posts', 'qa', 'photos'].map((dataType) => (
                                <Tooltip key={dataType} title={`${dataType.charAt(0).toUpperCase() + dataType.slice(1)} data available`}>
                                  <Chip
                                    icon={getDataIcon(dataType)}
                                    label={dataType}
                                    size="small"
                                    variant="outlined"
                                    className="modern-chip"
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                </Tooltip>
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View Profile Details">
                              <IconButton
                                size="small"
                                onClick={() => handleProfileClick(profile._id)}
                                sx={{ color: 'primary.main' }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default Dashboard; 